import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { LogIn, LogOut, Plus, RefreshCw } from 'lucide-react';
import { getToken } from '../lib/api';
import { useToast } from '../components/Toast';

const auth = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });
const fJson = async (url: string, init: RequestInit = {}) => {
  const r = await fetch(url, { ...init, headers: { ...auth().headers, ...(init.headers || {}) } });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
};

// Stratus uses `*77*` per the System → Dial Codes spec for hot-desking.
// Slot extensions are auto-allocated as `HD<5digits>` per migration 011 user_settings.is_hotdesk_slot.
export default function Hotdesk() {
  const qc = useQueryClient();
  const toast = useToast();
  const tenants = useQuery<any[]>({ queryKey: ['tenants'], queryFn: () => fJson('/api/tenants') });
  const [domain, setDomain] = useState<string>('');
  const active = domain || tenants.data?.[0]?.domain_uuid || '';

  const users = useQuery<any[]>({
    queryKey: ['users-list'],
    queryFn: () => fJson('/api/users'),
  });
  // Filter users that have hot-desk enabled vs slots vs neither
  const allInTenant = (users.data || []).filter((u: any) => u.domain_uuid === active);

  // For each user, check their settings for hotdesk fields. Settings are loaded individually,
  // so we batch-fetch settings for visible rows. (Quick-and-dirty: query all, server cap is fine for ≤500 users.)
  const settingsList = useQuery<any[]>({
    queryKey: ['user-settings-batch', active],
    queryFn: async () => {
      const out: any[] = [];
      for (const u of allInTenant) {
        try { const s = await fJson(`/api/users-extended/${u.extension_uuid}/settings`); out.push({ ...s, user_extension: u.extension, user_name: u.name }); } catch {}
      }
      return out;
    },
    enabled: !!active && !!allInTenant.length,
  });

  const slots = useMemo(() => (settingsList.data || []).filter(s => s.is_hotdesk_slot), [settingsList.data]);
  const hotdeskers = useMemo(() => (settingsList.data || []).filter(s => s.hotdesking_enabled && !s.is_hotdesk_slot), [settingsList.data]);

  const allocate = useMutation({
    mutationFn: () => fJson(`/api/users-extended/${active}/allocate-hotdesk-slot`, { method: 'POST' }),
    onSuccess: (r: any) => {
      toast('success', `Allocate ${r.slot_extension} as a new HD slot — create it in Users page with that extension number, then mark is_hotdesk_slot=true`);
    },
    onError: (e: any) => toast('error', e.message),
  });

  const login = useMutation({
    mutationFn: ({ user_uuid, slot_uuid }: any) => fJson(`/api/users-extended/${user_uuid}/hotdesk-login`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ slot_extension_uuid: slot_uuid }),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['user-settings-batch', active] }); toast('success', 'Logged in'); },
    onError: (e: any) => toast('error', e.message),
  });
  const logout = useMutation({
    mutationFn: (user_uuid: string) => fJson(`/api/users-extended/${user_uuid}/hotdesk-logout`, { method: 'POST' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['user-settings-batch', active] }); toast('success', 'Logged out'); },
    onError: (e: any) => toast('error', e.message),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Hot-Desking</h1>
        <select className="input max-w-xs" value={active} onChange={e => setDomain(e.target.value)}>
          {(tenants.data || []).map(t => <option key={t.domain_uuid} value={t.domain_uuid}>{t.domain_description || t.domain_name}</option>)}
        </select>
      </div>
      <p className="text-sm text-textSecondary">
        Roaming users dial <code className="bg-elevated px-1 font-mono">*77*&lt;extension&gt;</code> from any free hot-desk slot phone to claim it.
        Admin pre-allocates HD-numbered slot extensions; ordinary users get <i>hotdesking_enabled</i> on their main extension.
        Stratus uses a typed <code>is_hotdesk_slot</code> column rather than embedding magic prefixes in extension numbers.
      </p>

      <div className="grid grid-cols-2 gap-4">
        {/* Slots */}
        <div className="card p-0 overflow-hidden">
          <div className="p-3 border-b border-border flex items-center justify-between">
            <div className="font-medium">Hot-desk Slots</div>
            <button onClick={() => allocate.mutate()} className="btn-primary inline-flex items-center gap-1" disabled={!active}>
              <Plus size={14} /> Allocate next slot
            </button>
          </div>
          <table className="table">
            <thead><tr><th>Slot Ext</th><th>Status</th><th>Logged-in user</th><th>Since</th></tr></thead>
            <tbody>
              {slots.map((s: any) => (
                <tr key={s.extension_uuid} className="hover:bg-elevated">
                  <td className="font-mono">{s.user_extension}</td>
                  <td className={s.hotdesking_assignment_uuid ? 'text-success' : 'text-textSecondary'}>{s.hotdesking_assignment_uuid ? 'occupied' : 'free'}</td>
                  <td>{s.hotdesking_assignment_uuid ? <UserNameLink uuid={s.hotdesking_assignment_uuid} settingsList={settingsList.data || []} /> : '—'}</td>
                  <td className="text-xs">{s.hotdesking_logged_in_at ? new Date(s.hotdesking_logged_in_at).toLocaleString() : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!slots.length && <div className="p-6 text-center text-textSecondary text-sm">No slots yet. Click <b>Allocate next slot</b> to mint one.</div>}
        </div>

        {/* Users */}
        <div className="card p-0 overflow-hidden">
          <div className="p-3 border-b border-border font-medium">Hot-desk Enabled Users</div>
          <table className="table">
            <thead><tr><th>User</th><th>Currently at</th><th></th></tr></thead>
            <tbody>
              {hotdeskers.map((u: any) => {
                const slot = slots.find(s => s.extension_uuid === u.hotdesking_assignment_uuid);
                return (
                  <tr key={u.extension_uuid} className="hover:bg-elevated">
                    <td>{u.user_extension} — {u.user_name}</td>
                    <td className="font-mono">{slot ? slot.user_extension : <span className="text-textSecondary">— not logged in —</span>}</td>
                    <td>
                      {!slot ? (
                        <select className="input text-xs" defaultValue="" onChange={e => e.target.value && login.mutate({ user_uuid: u.extension_uuid, slot_uuid: e.target.value })}>
                          <option value="">Log in to slot…</option>
                          {slots.filter(s => !s.hotdesking_assignment_uuid).map(s => <option key={s.extension_uuid} value={s.extension_uuid}>{s.user_extension}</option>)}
                        </select>
                      ) : (
                        <button className="btn-ghost p-1 text-danger" onClick={() => logout.mutate(u.extension_uuid)}><LogOut size={14} /></button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!hotdeskers.length && <div className="p-6 text-center text-textSecondary text-sm">No users have hot-desking enabled. Toggle <code>hotdesking_enabled</code> on their settings.</div>}
        </div>
      </div>
    </div>
  );
}

function UserNameLink({ uuid, settingsList }: any) {
  const u = settingsList.find((s: any) => s.extension_uuid === uuid);
  return <span>{u ? `${u.user_extension} — ${u.user_name}` : uuid.slice(0, 8)}</span>;
}
